import {
  PharmacyItem as FormPharmacyItem,
  PrescriptionItem,
  TariffPackageInfo,
  PatientSourceWithTariffs,
  PharmacyFormValues,
  calculateFromInclusiveMRP,
  mapFormToPharmacyInvoicePayload,
  buildCreatePharmacyInvoiceParams,
} from './dispenseHelper';
import i18n from '../../services/i18n';

/**
 * Round number to two decimal places
 */
export const roundToTwo = (num: number): number => 
  Math.round((num + Number.EPSILON) * 100) / 100;

/**
 * Format date string to DD/MM/YYYY format
 */
export const formatDateToGB = (dateString: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
};

/**
 * Build taxes array from batch taxes object
 */
export const buildTaxesArray = (
  batchTaxes: { [key: string]: { percent?: number; amount?: number } } | undefined,
  totalMRPAmount: number
): Array<{ name: string; percent: number; amount: number }> => {
  if (batchTaxes) {
    // Build array from all tax keys in the object
    return Object.entries(batchTaxes)
      .filter(([, taxData]) => (taxData?.percent ?? 0) > 0)
      .map(([taxName, taxData]) => ({
        name: taxName.toUpperCase(),
        percent: taxData?.percent ?? 0,
        amount: (totalMRPAmount * (taxData?.percent ?? 0)) / 100,
      }));
  }
  return [];
};

/**
 * Calculate financial values for an item using taxes array
 * For tax-inclusive MRP pricing model
 */
type Tax = {
  tax_name: string;
  tax_percentage: number;
};

type FinancialResult = {
  totalMRP: number;
  sellingPricePerUnit: number;
  discount: number;
  taxableAmount: number;
  taxesWithAmounts: {
    tax_name: string;
    tax_percentage: number;
    tax_amount: number;
  }[];
  netAmount: number;
};

const round2 = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Calculate all monetary fields for a given quantity and discount.
 * 
 * EXCEL-STYLE CALCULATION: Rounds at each intermediate step per unit first.
 * 
 * Rationale:
 * - Matches legacy Excel-based invoice calculations that users verify against
 * - Prevents cumulative floating-point errors (e.g., 0.1 + 0.2 !== 0.3 in JS)
 * - Ensures line items sum exactly to totals (no "missing penny" discrepancies)
 * 
 * Alternative (rounding only final result) is simpler but can differ by 1-2 cents
 * from Excel/manual calculations, causing reconciliation issues.
 */
export function calcFinancials(
  baseMRP: number,
  qty: number,
  discountPercent: number,
  taxes: Tax[]
): FinancialResult {

  // 1️⃣ Total MRP (inclusive)
  const totalMRP = round2(baseMRP * qty);

  // 2️⃣ Calculate total tax %
  const totalTaxPercent = taxes.reduce(
    (sum, tax) => sum + (tax.tax_percentage || 0),
    0
  );

  // 3️⃣ Calculate Selling Price Per Unit (Tax Exclusive) - ROUND AT UNIT LEVEL
  const sellingPricePerUnit = round2(
    baseMRP / (1 + totalTaxPercent / 100)
  );

  // 4️⃣ Calculate Discount Per Unit - ROUND AT UNIT LEVEL
  const discountPerUnit = round2(
    sellingPricePerUnit * (discountPercent / 100)
  );

  // 5️⃣ Calculate Taxable Per Unit - ROUND AT UNIT LEVEL (ensure never negative)
  const taxablePerUnit = round2(
    Math.max(0, sellingPricePerUnit - discountPerUnit)
  );

  // 6️⃣ Multiply By Quantity
  const totalDiscount = round2(discountPerUnit * qty);
  const taxableAmount = round2(taxablePerUnit * qty);

  // 7️⃣ Calculate each tax separately on taxable amount
  const taxesWithAmounts = taxes.map((tax) => {
    const taxAmount = round2(
      taxableAmount * (tax.tax_percentage / 100)
    );

    return {
      tax_name: tax.tax_name,
      tax_percentage: tax.tax_percentage,
      tax_amount: Math.max(0, taxAmount),
    };
  });

  const totalTaxAmount = round2(
    taxesWithAmounts.reduce((sum, t) => sum + t.tax_amount, 0)
  );

  // 8️⃣ Final Net Amount (ensure never negative)
  let netAmount = round2(Math.max(0, taxableAmount + totalTaxAmount));
  
  // 9️⃣ Cap net amount to not exceed totalMRP (prevents rounding artifacts)
  // When discount is 0 or very small, netAmount should not exceed totalMRP
  if (netAmount > totalMRP) {
    netAmount = totalMRP;
  }

  return {
    totalMRP,
    sellingPricePerUnit,
    discount: totalDiscount,
    taxableAmount,
    taxesWithAmounts,
    netAmount,
  };
}

/**
 * Map prescription to form item
 */
export const mapPrescriptionToFormItem = (prescription: PrescriptionItem): FormPharmacyItem => {
  const availableBatches = (prescription.available_batches || []).map((batch) => {
    const batchMrp = batch.base_mrp ?? 0;
    // Build taxes array from batch.taxes object dynamically
    const batchTaxesArray = buildTaxesArray(
      batch.taxes as unknown as { [key: string]: { percent?: number; amount?: number } },
      batchMrp
    );
    
    return {
      batchId: batch.id ?? batch.batch_id,
      batchNumber: batch.batch_number,
      expiryDate: formatDateToGB(batch.expiry_date),
      isExpired: batch.is_expired,
      baseMrp: batch.base_mrp,
      baseCostPrice: batch.base_cost_price ?? batch.base_cost ?? null,
      availableStock: batch.available_stock,
      availableUomId: batch.available_uom_id,
      availableUom: batch.available_uom || batch.available_uom_name,
      storeId: batch.store_id,
      // Dynamic taxes array
      taxes: batchTaxesArray,
    };
  });

  const billedQty = prescription.remaining_qty || 0;

  // Check if item has package details
  const packageDetail = prescription.package_details?.[0];
  const isFromPackage = !!packageDetail;

  // Find the selected batch or first available batch to get pricing/tax info
  const selectedBatch = prescription.batch_code 
    ? availableBatches.find(b => b.batchNumber === prescription.batch_code)
    : availableBatches[0];
  
  // Calculate values based on selected batch
  const baseMRP = selectedBatch?.baseMrp ?? 0;
  const baseCostPrice = selectedBatch?.baseCostPrice ?? 0;
  const totalMRPAmount = baseMRP * billedQty;
  
  // Build base taxes array for calculation
  const baseTaxesArray = selectedBatch?.taxes && selectedBatch.taxes.length > 0
    ? selectedBatch.taxes
    : [];
  
  // Get discount percent from package if available
  const discountPercent = packageDetail?.discount_percent ?? 0;
  
  // Convert taxes to format expected by calcFinancials
  const itemTaxes = baseTaxesArray.map(t => ({
    tax_name: t.name,
    tax_percentage: t.percent,
  }));
  
  // Calculate using calcFinancials which applies discount
  const financials = calcFinancials(baseMRP, billedQty, discountPercent, itemTaxes);
  
  // Map taxes back to UI format
  const mappedTaxes = financials.taxesWithAmounts.map(t => ({
    name: t.tax_name,
    percent: t.tax_percentage,
    amount: t.tax_amount,
  }));

  return {
    prescriptionId: prescription.prescription_id,
    itemId: prescription.item_id,
    visitDate: formatDateToGB(prescription.visit_date),
    itemCode: prescription.item_code,
    itemName: prescription.item_name,
    batchCode: selectedBatch?.batchNumber ?? prescription.batch_code ?? '',
    batchId: selectedBatch?.batchId,
    expiryDate: selectedBatch?.expiryDate ?? formatDateToGB(prescription.expiry_date),
    manufacturer: prescription.manufacturer || '',
    availableBatches: availableBatches,
    prescribedStrength: prescription.prescribed_strength || prescription.strength || '',
    prescribedQty: prescription.prescribed_qty,
    prescribedUOM: prescription.prescribed_uom,
    billedStrength: prescription.strength || '',
    billedQty: billedQty,
    billedUOM: selectedBatch?.availableUom ?? prescription.billed_uom_code ?? '',
    rack: prescription.rack ?? '',
    shelf: prescription.shelf ?? '',
    container: prescription.container ?? '',
    checked: true,
    baseMRP: baseMRP,
    baseCostPrice: baseCostPrice,
    totalMRPAmount: totalMRPAmount,
    sellingPricePerUnit: financials.sellingPricePerUnit,
    totalSellingPrice: financials.taxableAmount,
    concessionAmount: financials.discount,
    concessionPercent: discountPercent,
    taxes: mappedTaxes,
    netAmount: financials.netAmount,
    pregnancyClass: prescription.pregnancy_class_code ?? '',
    provider: prescription.prescribed_by || '',
    availableStock: selectedBatch?.availableStock ?? prescription.available_stock,
    availableUomId: selectedBatch?.availableUomId ?? prescription.available_uom_id,
    availableUom: selectedBatch?.availableUom ?? prescription.available_uom,
    storeId: selectedBatch?.storeId,
    tariffId: null,
    tariffName: undefined,
    packageId: packageDetail?.package_id ?? null,
    packageName: packageDetail?.package_name,
    prescribed_remaining_qty: prescription.remaining_qty,
    uomConversions: prescription.uom_conversions || [],
    is_fromPackage: isFromPackage,
    package_pharmacy_item_id: packageDetail?.package_pharmacy_item_id ?? undefined,
    package_total_qty: packageDetail?.total_quantity ?? undefined,
    package_pending_qty: packageDetail?.quantity_remaining ?? undefined,
    package_pharmacy_remaining_amount: packageDetail?.pharmacy_remaining_amount,
    package_unit_price: packageDetail?.mrp ?? baseMRP,
    uom_description: packageDetail?.uom_description ?? undefined,
    package_details: prescription.package_details || [],
  };
};

/**
 * Calculate the effective package quantity considering both quantity and amount constraints.
 * 
 * Business Logic:
 * - package_pending_qty: Maximum units allowed by package quantity constraint
 * - package_pharmacy_remaining_amount: Remaining amount in package for pharmacy items
 * - netPricePerUnit: Actual net price per unit (after discount + taxes) that will be charged
 * 
 * @returns The actual package quantity that can be billed (minimum of qty and amount constraints)
 */
export const calculateEffectivePackageQty = (
  packagePendingQty: number,
  packageRemainingAmount: number | undefined,
  netPricePerUnit: number | undefined
): number => {
  // If no remaining amount info, use the quantity constraint only
  if (packageRemainingAmount === undefined || netPricePerUnit === undefined || netPricePerUnit <= 0) {
    return packagePendingQty;
  }
  
  // Calculate max units the remaining amount can cover based on actual net price
  const maxUnitsFromAmount = Math.floor(packageRemainingAmount / netPricePerUnit);
  
  // Effective package qty is the minimum of quantity and amount constraints
  return Math.min(packagePendingQty, maxUnitsFromAmount);
};

/**
 * Calculate actual net price per unit for package amount constraint checking.
 * Uses calculateFromInclusiveMRP for accurate tax-inclusive MRP calculations.
 * Discount is applied to MRP, not to selling price.
 */
export const calculateNetPricePerUnit = (
  baseMRP: number,
  discountPercent: number,
  taxes: { name?: string; percent?: number }[]
): number => {
  // Use calculateFromInclusiveMRP with qty=1 to get selling price per unit
  const taxesWithNames = taxes.map(t => ({ name: t.name || '', percent: t.percent || 0 }));
  const calc = calculateFromInclusiveMRP(baseMRP, 1, taxesWithNames);
  
  // Discount is applied to MRP, not selling price
  const discountAmount = baseMRP * discountPercent / 100;
  const discountedSellingPrice = calc.sellingPricePerUnit - discountAmount;
  const taxAmount = discountedSellingPrice * calc.totalTaxPercent / 100;
  
  return discountedSellingPrice + taxAmount;
};

/**
 * Split items into package and non-package items with calculated financials.
 * 
 * Validates BOTH quantity AND amount constraints from package_details.
 * - package_details[0].quantity_remaining: Item-specific qty limit
 * - package_details[0].pharmacy_remaining_amount: Shared amount pool across items
 * 
 * Allocation Rules:
 * 1. Check if item has package_details (not just is_fromPackage flag)
 * 2. Calculate effective package qty = min(billedQty, pkgPendingQty, qtyFromRemainingAmount)
 * 3. If effectivePkgQty >= billedQty → entire item to package
 * 4. If effectivePkgQty > 0 but < billedQty → SPLIT: effectivePkgQty to package, rest to non-package
 * 5. If effectivePkgQty = 0 → entire item to non-package
 * 
 * IMPORTANT: Remaining amount is SHARED across items in the same package.
 * 
 * ORDER DEPENDENCY: Items are processed sequentially in array order. Items earlier
 * in the array have priority for package allocation. Later items may receive less
 * or no package benefit if the shared remaining amount is exhausted by earlier items.
 * If consistent allocation is needed regardless of order, consider sorting items
 * by a deterministic criteria (e.g., item_id, price) before calling this function.
 */
export const splitItemsForBilling = (
  selectedFormItems: FormPharmacyItem[]
): { packageItems: FormPharmacyItem[]; nonPackageItems: FormPharmacyItem[] } => {
  const packageItems: FormPharmacyItem[] = [];
  const nonPackageItems: FormPharmacyItem[] = [];

  // Track cumulative remaining amount per package (SHARED across items)
  // Key: packageId, Value: remainingAmount
  const packageRemainingAmounts: Map<number, number> = new Map();

  // First pass: Initialize package remaining amounts from package_details
  selectedFormItems.forEach(item => {
    const packageDetail = item.package_details?.[0];
    if (packageDetail?.package_id) {
      const pkgId = packageDetail.package_id;
      if (!packageRemainingAmounts.has(pkgId)) {
        // Initialize with the shared pharmacy_remaining_amount
        packageRemainingAmounts.set(pkgId, packageDetail.pharmacy_remaining_amount ?? Infinity);
      }
    }
  });

  selectedFormItems.forEach(item => {
    const billedQty = item.billedQty ?? 0;
    if (billedQty <= 0) return;

    // Check package_details for package eligibility (not just is_fromPackage flag)
    const packageDetail = item.package_details?.[0];
    const hasValidPackageDetails = !!packageDetail?.package_id;

    // Map TaxDetail[] to Tax[] format expected by calcFinancials
    const itemTaxes: Tax[] = (item.taxes || []).map(t => ({
      tax_name: t.name,
      tax_percentage: t.percent,
    }));

    // Helper to convert taxesWithAmounts back to TaxDetail format
    const toTaxDetail = (taxes: { tax_name: string; tax_percentage: number; tax_amount: number }[]) =>
      taxes.map(t => ({ name: t.tax_name, percent: t.tax_percentage, amount: t.tax_amount }));

    // If no valid package details, entire item goes to non-package
    if (!hasValidPackageDetails) {
      // Preserve item's existing concession if set
      const existingDiscountPercent = item.concessionPercent ?? 0;
      const { totalMRP, discount, taxesWithAmounts, netAmount } = calcFinancials(
        item.baseMRP, billedQty, existingDiscountPercent, itemTaxes
      );
      nonPackageItems.push({
        ...item,
        totalMRPAmount: totalMRP,
        concessionAmount: discount,
        concessionPercent: existingDiscountPercent,
        taxes: toTaxDetail(taxesWithAmounts),
        netAmount,
        is_fromPackage: false,
      });
      return;
    }

    // Extract package info from package_details
    const pkgId = packageDetail.package_id;
    const pkgPendingQty = packageDetail.quantity_remaining ?? 0;
    // Use package discount if available, otherwise fall back to item's existing concession
    const discountPercent = packageDetail.discount_percent ?? item.concessionPercent ?? 0;

    // Calculate net price per unit WITH discount for amount constraint check
    const netPricePerUnit = calcFinancials(item.baseMRP, 1, discountPercent, itemTaxes);

    // Get current remaining amount for this package
    const remainingAmount = packageRemainingAmounts.get(pkgId) ?? Infinity;

    // Calculate max units allowed by remaining amount
    const maxUnitsFromAmount = remainingAmount !== Infinity && netPricePerUnit.netAmount > 0
      ? Math.floor(remainingAmount / netPricePerUnit.netAmount)
      : Infinity;

    // Effective package qty = min(billedQty, pkgPendingQty, maxUnitsFromAmount)
    const effectivePkgQty = Math.min(billedQty, pkgPendingQty, maxUnitsFromAmount);

    if (effectivePkgQty >= billedQty) {
      // CASE 1: Entire item fits in package (both qty and amount constraints satisfied)
      const { totalMRP, discount, taxesWithAmounts, netAmount } = calcFinancials(
        item.baseMRP, billedQty, discountPercent, itemTaxes
      );

      // Deduct from shared remaining amount
      packageRemainingAmounts.set(pkgId, remainingAmount - netAmount);

      packageItems.push({
        ...item,
        totalMRPAmount: totalMRP,
        concessionAmount: discount,
        concessionPercent: discountPercent,
        taxes: toTaxDetail(taxesWithAmounts),
        netAmount,
        is_fromPackage: true,
        packageId: pkgId,
        packageName: packageDetail.package_name,
        package_pharmacy_item_id: packageDetail.package_pharmacy_item_id ?? undefined,
        package_total_qty: packageDetail.total_quantity ?? undefined,
        package_pending_qty: packageDetail.quantity_remaining ?? undefined,
        package_pharmacy_remaining_amount: packageDetail.pharmacy_remaining_amount,
        package_unit_price: packageDetail.mrp ?? item.baseMRP,
      });
    } else if (effectivePkgQty > 0) {
      // CASE 2: SPLIT - Part goes to package, rest to non-package

      // Package portion (with discount)
      const { totalMRP: pkgTotalMRP, discount: pkgDiscount, taxesWithAmounts: pkgTaxes, netAmount: pkgNetAmount } = calcFinancials(
        item.baseMRP, effectivePkgQty, discountPercent, itemTaxes
      );

      // Deduct from shared remaining amount
      packageRemainingAmounts.set(pkgId, remainingAmount - pkgNetAmount);

      packageItems.push({
        ...item,
        billedQty: effectivePkgQty,
        totalMRPAmount: pkgTotalMRP,
        concessionAmount: pkgDiscount,
        concessionPercent: discountPercent,
        taxes: toTaxDetail(pkgTaxes),
        netAmount: pkgNetAmount,
        is_fromPackage: true,
        packageId: pkgId,
        packageName: packageDetail.package_name,
        package_pharmacy_item_id: packageDetail.package_pharmacy_item_id ?? undefined,
        package_total_qty: packageDetail.total_quantity ?? undefined,
        package_pending_qty: packageDetail.quantity_remaining ?? undefined,
        package_pharmacy_remaining_amount: packageDetail.pharmacy_remaining_amount,
        package_unit_price: packageDetail.mrp ?? item.baseMRP,
      });

      // Non-package portion (no discount)
      const nonPkgQty = billedQty - effectivePkgQty;
      const { totalMRP: nonPkgTotalMRP, discount: nonPkgDiscount, taxesWithAmounts: nonPkgTaxes, netAmount: nonPkgNetAmount } = calcFinancials(
        item.baseMRP, nonPkgQty, 0, itemTaxes
      );

      const itemWithoutPackageFields = {
        ...item,
        packageId: undefined,
        packageName: undefined,
        package_pharmacy_item_id: undefined,
        package_total_qty: undefined,
        package_pending_qty: undefined,
        package_pharmacy_remaining_amount: undefined,
        package_unit_price: undefined,
      };

      nonPackageItems.push({
        ...itemWithoutPackageFields,
        billedQty: nonPkgQty,
        totalMRPAmount: nonPkgTotalMRP,
        concessionAmount: nonPkgDiscount,
        concessionPercent: 0,
        taxes: toTaxDetail(nonPkgTaxes),
        netAmount: nonPkgNetAmount,
        is_fromPackage: false,
      });
    } else {
      // CASE 3: effectivePkgQty = 0, entire item to non-package
      // Preserve item's existing concession if package constraints are exhausted
      const existingDiscountPercent = item.concessionPercent ?? 0;
      const { totalMRP, discount, taxesWithAmounts, netAmount } = calcFinancials(
        item.baseMRP, billedQty, existingDiscountPercent, itemTaxes
      );

      const itemWithoutPackageFields = {
        ...item,
        packageId: undefined,
        packageName: undefined,
        package_pharmacy_item_id: undefined,
        package_total_qty: undefined,
        package_pending_qty: undefined,
        package_pharmacy_remaining_amount: undefined,
        package_unit_price: undefined,
      };

      nonPackageItems.push({
        ...itemWithoutPackageFields,
        totalMRPAmount: totalMRP,
        concessionAmount: discount,
        concessionPercent: existingDiscountPercent,
        taxes: toTaxDetail(taxesWithAmounts),
        netAmount,
        is_fromPackage: false,
      });
    }
  });

  return { packageItems, nonPackageItems };
};

/**
 * Build invoice payload for API
 */
export const buildInvoicePayload = (
  formValues: PharmacyFormValues,
  items: FormPharmacyItem[],
  patientId: number,
  tariffPackageInfo: TariffPackageInfo | undefined,
  selectedStoreId: number | null
) => {
  const treatmentDoctorId = 0;
  const visitDate = new Date().toISOString().split('T')[0];

  return mapFormToPharmacyInvoicePayload(
    { ...formValues, items },
    patientId,
    0, // appointmentId
    treatmentDoctorId,
    visitDate,
    tariffPackageInfo,
    'dispensed',
    true,
    selectedStoreId,
  );
};

/**
 * Get tariff and package info from form values and selected items
 */
export const getTariffPackageInfo = (
  formValues: PharmacyFormValues,
  patientSourceOptions: PatientSourceWithTariffs[],
  selectedFormItems: FormPharmacyItem[]
): TariffPackageInfo => {
  const selectedSource = patientSourceOptions.find(src => src.value === formValues.patientSource);
  const selectedTariff = selectedSource?.tariffs?.find(t => t.tariff_code === formValues.tariff);
  
  // Get package info from first item that has package (package info comes from items via API)
  const itemWithPackage = selectedFormItems.find(item => item.is_fromPackage && item.packageId);
  
  return {
    tariffId: selectedTariff?.tariff_id ?? null,
    tariffName: selectedTariff?.tariff_name,
    packageId: itemWithPackage?.packageId ?? null,
    packageName: itemWithPackage?.packageName,
  };
};

/**
 * Submit pharmacy dispense form
 */
export interface SubmitDispenseParams {
  formValues: PharmacyFormValues;
  patientId: string;
  patientSourceOptions: PatientSourceWithTariffs[];
  selectedStoreId: number | null;
  createInvoiceMutation: {
    mutateAsync: (params: ReturnType<typeof buildCreatePharmacyInvoiceParams>) => Promise<unknown>;
  };
  onSuccess: () => void;
  onError: (message: string) => void;
  onWarning: (message: string) => void;
}

export const submitPharmacyDispense = async ({
  formValues,
  patientId,
  patientSourceOptions,
  selectedStoreId,
  createInvoiceMutation,
  onSuccess,
  onError,
  onWarning,
}: SubmitDispenseParams): Promise<void> => {
  if (!patientId) {
    onError(
      i18n.t('pharmacy_dispense.errors.missing_required_id', {
        ns: 'billing_config',
        defaultValue: 'Missing required ID: patientId={{patientId}}',
        patientId,
      })
    );
    return;
  }

  const selectedFormItems = formValues.items.filter((item) => item.checked);
  if (selectedFormItems.length === 0) {
    onWarning(
      i18n.t('pharmacy_dispense.warnings.select_at_least_one_item_billing', {
        ns: 'billing_config',
        defaultValue: 'Please select at least one item for billing',
      })
    );
    return;
  }

  const tariffPackageInfo = getTariffPackageInfo(formValues, patientSourceOptions, selectedFormItems);
  const { packageItems, nonPackageItems } = splitItemsForBilling(selectedFormItems);

  if (packageItems.length === 0 && nonPackageItems.length === 0) {
    onWarning(
      i18n.t('pharmacy_dispense.warnings.no_valid_items_to_bill', {
        ns: 'billing_config',
        defaultValue: 'No valid items to bill',
      })
    );
    return;
  }

  const mutations: Promise<void>[] = [];

  if (packageItems.length > 0) {
    const pkgPayload = buildInvoicePayload(
      formValues,
      packageItems,
      Number(patientId),
      tariffPackageInfo,
      selectedStoreId
    );

    const mutationPromise = createInvoiceMutation
      .mutateAsync(buildCreatePharmacyInvoiceParams(pkgPayload))
      .then(() => {})
      .catch((error: Error) => {
        onError(
          i18n.t('pharmacy_dispense.errors.failed_create_invoice_package', {
            ns: 'billing_config',
            defaultValue: 'Failed to create pharmacy invoice (package): {{error}}',
            error: error.message || i18n.t('pharmacy_dispense.unknown_error', { ns: 'billing_config', defaultValue: 'Unknown error' }),
          })
        );
        throw error;
      });
    mutations.push(mutationPromise);
  }

  if (nonPackageItems.length > 0) {
    const nonPkgPayload = buildInvoicePayload(
      formValues,
      nonPackageItems,
      Number(patientId),
      undefined, // No tariff/package info for non-package items
      selectedStoreId
    );
    
    const mutationPromise = createInvoiceMutation
      .mutateAsync(buildCreatePharmacyInvoiceParams(nonPkgPayload))
      .then(() => {})
      .catch((error: Error) => {
        onError(
          i18n.t('pharmacy_dispense.errors.failed_create_invoice_non_package', {
            ns: 'billing_config',
            defaultValue: 'Failed to create pharmacy invoice (non-package): {{error}}',
            error: error.message || i18n.t('pharmacy_dispense.unknown_error', { ns: 'billing_config', defaultValue: 'Unknown error' }),
          })
        );
        throw error;
      });
    mutations.push(mutationPromise);
  }

  try {
    await Promise.all(mutations);
    onSuccess();
  } catch {
    // Error already handled in individual catches
  }
};

/**
 * Map available batches from API item to form format
 */
export const mapApiBatchesToFormBatches = (
  batches: Array<{
    id: number;
    batch_number: string;
    expiry_date: string;
    base_mrp: number;
    base_cost?: number;
    sgst_percent?: number;
    cgst_percent?: number;
    available_stock: number;
    available_uom_id?: number;
    available_uom_name?: string;
    store_id?: number;
    taxes?: { [key: string]: { percent?: number; amount?: number } };
  }>,
  fallbackSgst: number,
  fallbackCgst: number,
  fallbackUom?: string
) => {
  return batches.map((b) => {
    // Build taxes array from batch.taxes object or legacy fields
    const batchTaxesArray = buildTaxesArray(
      b.taxes as { [key: string]: { percent?: number; amount?: number } } | undefined,
      b.base_mrp
    );
    // If no taxes from taxes object, build from legacy percent fields
    const taxes = batchTaxesArray.length > 0 ? batchTaxesArray : [
      { name: 'SGST', percent: b.sgst_percent ?? fallbackSgst, amount: 0 },
      { name: 'CGST', percent: b.cgst_percent ?? fallbackCgst, amount: 0 },
    ].filter(t => t.percent > 0);

    return {
      batchId: b.id,
      batchNumber: b.batch_number,
      expiryDate: formatDateToGB(b.expiry_date),
      isExpired: new Date(b.expiry_date) < new Date(),
      baseMrp: b.base_mrp,
      baseCostPrice: b.base_cost ?? null,
      taxes,
      availableStock: b.available_stock,
      availableUomId: b.available_uom_id,
      availableUom: b.available_uom_name ?? fallbackUom,
      storeId: b.store_id,
    };
  });
};

/**
 * Map UOM conversions from API to form format
 */
export const mapApiUomConversions = (
  uomConversions: Array<{
    id?: number | null;
    item_id?: number;
    item_name?: string;
    uom_id?: number;
    uom_code?: string;
    uom_static_field?: string;
    code?: string;
    conversion_factor?: number;
    converted_mrp?: number;
    converted_cost_price?: number;
  }>
) => {
  return uomConversions.map((uom) => ({
    id: uom.id ?? 0,
    item_id: uom.item_id ?? 0,
    item_name: uom.item_name ?? '',
    uom_id: uom.uom_id ?? 0,
    uom_code: uom.uom_code ?? '',
    uom: uom.uom_code ?? '',
    uom_static_field: uom.uom_static_field ?? '',
    code: uom.code ?? '',
    conversion_factor: uom.conversion_factor ?? 1,
    converted_mrp: uom.converted_mrp ?? 0,
    converted_cost_price: uom.converted_cost_price ?? 0,
  }));
};

/**
 * Create a new form item from batch selection
 */
export interface BatchSelectParams {
  batch: {
    id: number;
    batch_number: string;
    expiry_date: string;
    base_mrp: number;
    base_cost_price?: number;
    sgst_percent?: number;
    cgst_percent?: number;
    available_stock: number;
    available_uom_id?: number;
    available_uom_name?: string;
    store_id?: number;
  };
  apiItem: {
    item_id: number;
    item_code: string;
    item_name: string;
    item_group_name?: string;
    base_uom_name?: string;
    rack_name?: string;
    shelf_name?: string;
    bin_name?: string;
    SGST?: number;
    CGST?: number;
    batches?: Array<{
      id: number;
      batch_number: string;
      expiry_date: string;
      base_mrp: number;
      base_cost?: number;
      sgst_percent?: number;
      cgst_percent?: number;
      available_stock: number;
      available_uom_id?: number;
      available_uom_name?: string;
      store_id?: number;
    }>;
  };
  existingItemInList?: FormPharmacyItem;
}

export const createFormItemFromBatch = ({
  batch,
  apiItem,
  existingItemInList,
}: BatchSelectParams): FormPharmacyItem => {
  const baseMRP = batch.base_mrp || 0;
  const billedQty = 1;
  const totalMRPAmount = baseMRP * billedQty;
  
  // Build taxes array from API data
  const baseTaxes = [
    { name: 'SGST', percent: batch.sgst_percent ?? apiItem.SGST ?? 0, amount: 0 },
    { name: 'CGST', percent: batch.cgst_percent ?? apiItem.CGST ?? 0, amount: 0 },
  ].filter(t => t.percent > 0);
  
  // Calculate using tax-inclusive MRP formula
  const taxCalc = calculateFromInclusiveMRP(baseMRP, billedQty, baseTaxes);

  const availableBatches = mapApiBatchesToFormBatches(
    apiItem.batches || [],
    apiItem.SGST ?? 0,
    apiItem.CGST ?? 0,
    apiItem.base_uom_name
  );

  return {
    prescriptionId: 0,
    itemId: apiItem.item_id,
    visitDate: new Date().toLocaleDateString('en-GB'),
    itemCode: apiItem.item_code,
    itemName: apiItem.item_name,
    batchCode: batch.batch_number,
    batchId: batch.id,
    expiryDate: formatDateToGB(batch.expiry_date),
    manufacturer: apiItem.item_group_name || '',
    availableBatches: availableBatches,
    prescribedStrength: existingItemInList?.prescribedStrength || '500 Mg',
    prescribedQty: existingItemInList?.prescribedQty || 24,
    prescribedUOM: existingItemInList?.prescribedUOM || apiItem.base_uom_name || '',
    prescribed_remaining_qty: existingItemInList?.prescribed_remaining_qty || existingItemInList?.prescribedQty || 24,
    billedStrength: '',
    billedQty: billedQty,
    billedUOM: apiItem.base_uom_name || '',
    rack: apiItem.rack_name || '',
    shelf: apiItem.shelf_name || '',
    container: apiItem.bin_name || '',
    checked: true,
    baseMRP: baseMRP,
    baseCostPrice: batch.base_cost_price || 0,
    totalMRPAmount: totalMRPAmount,
    sellingPricePerUnit: taxCalc.sellingPricePerUnit,
    totalSellingPrice: taxCalc.totalSellingPrice,
    concessionAmount: 0,
    concessionPercent: 0,
    taxes: taxCalc.taxesWithAmounts,
    netAmount: taxCalc.netAmount,
    pregnancyClass: '',
    provider: '',
    availableStock: batch.available_stock,
    availableUomId: batch.available_uom_id,
    availableUom: batch.available_uom_name ?? apiItem.base_uom_name,
    storeId: batch.store_id,
    tariffId: null,
    tariffName: undefined,
    packageId: null,
    packageName: undefined,
  };
};

/**
 * Create a replacement item from API item (preserving prescription info)
 */
export interface ItemReplaceParams {
  apiItem: {
    item_id: number;
    item_code: string;
    item_name: string;
    item_group_name?: string;
    base_uom_name?: string;
    rack_name?: string;
    shelf_name?: string;
    bin_name?: string;
    SGST?: number;
    CGST?: number;
    batches?: Array<{
      id: number;
      batch_number: string;
      expiry_date: string;
      base_mrp: number;
      base_cost?: number;
      sgst_percent?: number;
      cgst_percent?: number;
      available_stock: number;
      available_uom_id?: number;
      available_uom_name?: string;
      store_id?: number;
    }>;
    uom_conversions?: Array<{
      id?: number | null;
      item_id?: number;
      item_name?: string;
      uom_id?: number;
      uom_code?: string;
      uom_static_field?: string;
      code?: string;
      conversion_factor?: number;
      converted_mrp?: number;
      converted_cost_price?: number;
    }>;
  };
  originalItem: FormPharmacyItem;
}

export const createReplacementItem = ({
  apiItem,
  originalItem,
}: ItemReplaceParams): FormPharmacyItem => {
  const availableBatches = mapApiBatchesToFormBatches(
    apiItem.batches || [],
    apiItem.SGST ?? 0,
    apiItem.CGST ?? 0,
    apiItem.base_uom_name
  );

  const uomConversions = mapApiUomConversions(apiItem.uom_conversions || []);

  return {
    ...originalItem,
    itemId: apiItem.item_id,
    itemCode: apiItem.item_code,
    itemName: apiItem.item_name,
    manufacturer: apiItem.item_group_name || '',
    availableBatches: availableBatches,
    uomConversions: uomConversions,
    prescribedStrength: originalItem.prescribedStrength,
    prescribedQty: originalItem.prescribedQty,
    prescribedUOM: originalItem.prescribedUOM,
    prescribed_remaining_qty: originalItem.prescribed_remaining_qty,
    batchCode: '',
    batchId: undefined,
    expiryDate: '',
    billedQty: 0,
    billedUOM: '',
    baseMRP: 0,
    baseCostPrice: 0,
    totalMRPAmount: 0,
    sellingPricePerUnit: 0,
    totalSellingPrice: 0,
    concessionAmount: 0,
    concessionPercent: 0,
    taxes: [],
    netAmount: 0,
    rack: apiItem.rack_name || '',
    shelf: apiItem.shelf_name || '',
    container: apiItem.bin_name || '',
    availableStock: undefined,
    availableUomId: undefined,
    availableUom: apiItem.base_uom_name,
    storeId: undefined,
  };
};
